/**
 * 멈춘 그림을 붙들어 둔다 — 화면이 찍어 올린 그림도, 영상에서 떠낸 한 장면도.
 *
 * **찍어 올린 그림을 붙드는 이유.** 서버는 화면이 새 그림을 올릴 때마다 직전 그림
 * 파일을 지운다(화면당 최신 하나만 남긴다). 콘솔은 들어온 순간의 그림을 멈춰 두는데,
 * 그 주소를 `<img>` 에 그대로 걸면 몇 초 뒤 파일이 사라져 **나중에 그려지는 것 —
 * 스크롤해야 보이는 카드, 접었다 편 묶음, 크게 보기 — 이 깨진 그림이 된다.** 받는
 * 즉시 바이트를 브라우저에 붙들어 둔다.
 *
 * **영상에서 장면을 떠내는 이유.** 화면은 영상일 때 그림 대신 재생 위치만 보고한다
 * (찍어 보내면 수십 KB, 위치는 몇 바이트). 같은 영상을 받아 그 위치로 옮겨 캔버스에
 * 그리고 JPEG 로 만든다. 멈춘 `<video>` 를 그대로 걸면 카드 수만큼 영상을 붙들고,
 * 보는 사람에게는 캡처가 아니라 틀 수 있는 것으로 보인다.
 */

/**
 * 서버가 그 그림이 없다고 했다 — 이미 지워졌거나 서명이 끝났다. 새로 찍어야 한다.
 */
export const MISSING = Symbol('missing');

/**
 * 붙든 결과.
 *
 *   - 문자열 — 붙든 그림의 주소(`blob:`).
 *   - `MISSING` — 서버에 그 그림이 없다.
 *   - `null` — 받지 못했다(다른 오리진인데 CORS 를 허락하지 않는 경우 등). 원래 주소로 대신한다.
 */
export type Still = string | typeof MISSING | null;

/** 떠낼 그림의 최대 너비. 크게 보기에서도 흐려 보이지 않을 만큼. */
const MAX_WIDTH = 1280;
const JPEG_QUALITY = 0.85;

/** 한 장면을 떠내는 데 기다리는 한도. 없으면 회선이 막힌 카드가 영영 검은 상자로 남는다. */
const TIMEOUT_MS = 15_000;

/**
 * 한꺼번에 떠내는 영상 수.
 *
 * 벽에 들어온 순간 카드 수십 장이 동시에 영상을 받기 시작하면 회선과 디코더가 한꺼번에
 * 묶여 첫 장조차 늦게 뜬다. 몇 장씩 차례로 떠낸다. 찍어 올린 그림은 작고 곧 지워지므로
 * 줄 세우지 않는다.
 */
const CONCURRENCY = 4;

/**
 * 붙들어 둘 그림 수. 벽 한 번(최대 200대)을 넉넉히 담는다.
 *
 * 같은 그림은 다시 받지 않는다 — 크게 보기를 열거나 카드가 다시 그려질 때마다 또 받으면
 * 안 된다. 넘치면 오래된 것부터 놓되, 지금 걸려 있는 것은 놓지 않는다(`holdStill`).
 */
const MAX_STILLS = 300;

const stills = new Map<string, Promise<Still>>();

/**
 * 지금 그려져 있는 그림 — 키마다 몇 곳이 걸고 있나.
 *
 * **걸려 있는 그림은 놓지 않는다.** 예전에는 넘치면 가장 오래된 것을 무조건 놓았는데
 * (`revokeObjectURL`), 그 주소는 아직 `<img>` 에 걸려 있을 수 있다 — 콘텐츠 격자를 영상
 * 300개 너머까지 내렸다가 돌아오면 앞쪽 카드가 깨진 그림이 됐다. 카드는 한 번 받은 주소를
 * 들고 있을 뿐 다시 받지 않으므로, 놓는 쪽이 "아무도 안 건 것" 만 골라야 한다.
 */
const holds = new Map<string, number>();

/**
 * 이 그림을 걸었다 — 놓을 때 부를 함수를 돌려준다.
 *
 * 그림을 받기 **전에** 건다. 받는 사이에 다른 그림이 들어와 넘치면, 걸지 않은 그림은 막 받은
 * 참에 놓일 수 있다.
 */
export function holdStill(key: string): () => void {
  holds.set(key, (holds.get(key) ?? 0) + 1);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const left = (holds.get(key) ?? 1) - 1;
    if (left > 0) holds.set(key, left);
    else holds.delete(key);
    // 걸려 있어서 못 놓았던 것이 이제 놓일 수 있다.
    trim();
  };
}

/**
 * 넘친 만큼 놓을 키 — 오래된 것부터, **걸려 있는 것은 건너뛴다.**
 *
 * 전부 걸려 있으면 덜 놓는다(빈 배열일 수도 있다). 그 그림들은 화면에 떠 있는 것이라
 * 어차피 브라우저가 들고 있다 — 놓아 봐야 깨진 그림만 남는다.
 */
export function overflowKeys(order: Iterable<string>, isHeld: (key: string) => boolean, max: number): string[] {
  const keys = [...order];
  let excess = keys.length - max;
  const drop: string[] = [];
  for (const key of keys) {
    if (excess <= 0) break;
    if (isHeld(key)) continue;
    drop.push(key);
    excess -= 1;
  }
  return drop;
}

function trim(): void {
  for (const key of overflowKeys(stills.keys(), (candidate) => holds.has(candidate), MAX_STILLS)) {
    const dropped = stills.get(key);
    stills.delete(key);
    void dropped?.then((still) => {
      if (typeof still === 'string') URL.revokeObjectURL(still);
    });
  }
}

/**
 * 장면을 뜨며 읽은 영상 길이(초) — 키마다.
 *
 * 서버는 아직 영상 길이를 모른다(`duration_sec` 은 트랜스코드가 붙어야 채워진다). 장면을
 * 뜨려고 어차피 머리를 읽으니 그때 함께 적어 둔다 — 콘텐츠 칸의 길이 표시가 이것을 쓴다.
 */
const lengths = new Map<string, number>();

/** `videoFrame` 이 그 키로 뜰 때 읽은 영상 길이. 아직 안 떴거나 못 읽었으면 null. */
export function videoLength(key: string): number | null {
  return lengths.get(key) ?? null;
}

let active = 0;
const queue: Array<() => void> = [];

/**
 * 같은 `key` 는 한 번만 받는다.
 *
 * 붙들지 못한 결과는 기억하지 않는다 — 회선이 잠깐 끊겼던 그림이 다음에 그려질 때
 * 다시 해 본다.
 */
function remember(key: string, job: () => Promise<Still>): Promise<Still> {
  const known = stills.get(key);
  if (known) return known;

  const pending = job();
  stills.set(key, pending);
  void pending.then((still) => {
    if (typeof still !== 'string' && stills.get(key) === pending) stills.delete(key);
  });

  if (stills.size > MAX_STILLS) trim();
  return pending;
}

/**
 * 화면이 찍어 올린 그림을 받아 붙든다.
 *
 * 주소는 처음 부를 때의 것만 쓴다 — 서명 URL 은 조회마다 문자열이 달라진다.
 */
export function frameImage(key: string, url: string): Promise<Still> {
  return remember(key, async () => {
    let response: Response;
    try {
      response = await fetch(url, { credentials: 'omit' });
    } catch {
      return null;
    }
    if (!response.ok) return MISSING;
    try {
      return URL.createObjectURL(await response.blob());
    } catch {
      return null;
    }
  });
}

/**
 * 영상의 한 장면을 떠내 붙든다.
 *
 * 영상 파일은 그림처럼 갈아 끼워지지 않으므로 `MISSING` 이 없다 — 못 떠내면 null 이다.
 * `atSec` 이 함수면 영상 길이를 읽은 뒤 위치를 정한다(길이를 미리 모를 때).
 */
export function videoFrame(key: string, url: string, atSec: number | ((duration: number) => number)): Promise<Still> {
  return remember(key, () =>
    limited(() =>
      grab(url, atSec, (duration) => {
        lengths.set(key, duration);
      }),
    ),
  );
}

function limited<T>(job: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const run = (): void => {
      active += 1;
      job()
        .then(resolve, reject)
        .finally(() => {
          active -= 1;
          queue.shift()?.();
        });
    };
    if (active < CONCURRENCY) run();
    else queue.push(run);
  });
}

function grab(
  url: string,
  atSec: number | ((duration: number) => number),
  onLength: (duration: number) => void,
): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    let settled = false;

    const settle = (src: string | null): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      // 받던 것과 디코더를 바로 놓는다. 요소를 버리기만 하면 브라우저가 한동안 붙들고 있다.
      video.removeAttribute('src');
      video.load();
      resolve(src);
    };
    const timer = window.setTimeout(() => settle(null), TIMEOUT_MS);

    const draw = (): void => {
      void encode(video).then(settle, () => settle(null));
    };

    /*
     * **CORS 로 받는다.** 콘솔과 저장소의 오리진이 다를 때(사내 주소로 콘솔을 연 경우 등)
     * 그냥 받으면 캔버스가 오염되어 JPEG 로 꺼내는 순간 막힌다. 저장소가 CORS 를 허락하지
     * 않으면 받기 자체가 실패하는데, 그때는 null 을 돌려 부르는 쪽이 대신할 것을 건다.
     */
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.addEventListener('error', () => settle(null), { once: true });
    video.addEventListener(
      'loadedmetadata',
      () => {
        video.addEventListener(
          'seeked',
          () => {
            // 옮긴 자리의 그림이 아직 없으면 준비될 때까지 기다린다 — 그대로 그리면 검은 그림이 된다.
            if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) draw();
            else video.addEventListener('canplay', draw, { once: true });
          },
          { once: true },
        );
        const known = Number.isFinite(video.duration) && video.duration > 0;
        if (known) onLength(video.duration);
        const at = typeof atSec === 'function' ? atSec(video.duration) : atSec;
        // 끝을 넘긴 위치는 영상 끝 바로 앞으로 누른다.
        const end = known ? video.duration - 0.05 : at;
        video.currentTime = Math.max(0, Math.min(at, end));
      },
      { once: true },
    );
    video.src = url;
  });
}

async function encode(video: HTMLVideoElement): Promise<string | null> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) return null;

  const scale = Math.min(1, MAX_WIDTH / width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise<Blob | null>((resolve) => {
    try {
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
    } catch {
      // 오염된 캔버스(SecurityError). 부르는 쪽이 대신할 것을 건다.
      resolve(null);
    }
  });
  return blob ? URL.createObjectURL(blob) : null;
}
